/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React, {Fragment} from 'react';
import {Dataset, DatasetRow} from "../data/Dataset";
import {LayerDef} from "../types";
import {XIcon} from "../ui/XIcon";
import {IconInfo} from "../util/IconInfo";
import {Button, CaretLeftIcon, CaretRightIcon, DotIcon, Theme} from "@ice/components";
import {LayerUtil} from "./LayerUtil";
import {isValidNumber} from "../util/CommonUtil";
import {$t} from "../lang/LangUtil";


interface MapTooltipProps {
    x: number;
    y: number;
    layerDef: LayerDef;
    data: DatasetRow[];
    pinned?: boolean;
    onEscape?: () => void;
}


interface MapTooltipState {
    selectedIndex: number;
    showDetails: boolean;
}


export default class MapTooltip extends React.Component<MapTooltipProps, MapTooltipState> {

    constructor(props: MapTooltipProps) {
        super(props);
        this.state = {
            selectedIndex: this.props.data.length === 1 ? 0 : undefined,
            showDetails: false
        };
    }

    private renderDetailedView = (dataRow: DatasetRow, dataset: Dataset) => {
        const rows = [];
        for (const field of dataset.getFieldDefs()) {
            rows.push(
                <div
                    css={(theme: Theme)=>css`
                      display: flex;
                      flex-direction: row;
                      align-items: center;
                      padding-bottom: 0.2em;
                    `}
                >
                    <span className={"fieldname"}>
                        {field.name}
                    </span>
                        <div style={{flexGrow: 1}} />
                        <span className={"fieldvalue"}>
                        {dataRow.getValue(field.id)}
                    </span>
                </div>
            )
        }
        return (
            <div style={{overflowY: "auto", maxHeight: "16em"}}>
                {rows}
            </div>
        )
    }

    private renderSimpleView = (dataRow: DatasetRow, dataset: Dataset) => {
        const layerDef = this.props.layerDef;
        const colorFieldId = layerDef.config.colorFieldId;
        const tooltipRows = [];
        if (layerDef.config.tooltipFields) {
            for (const ttf of layerDef.config.tooltipFields) {
                let fieldDef = dataset.getFieldDefById(ttf);
                if (fieldDef) {
                    let value = dataRow.getValue(fieldDef.id);
                    tooltipRows.push(
                        <div className={"tooltipfieldrow"}>
                            <span className={"fieldname"}>{fieldDef.name}</span>
                            <span style={{flexGrow: 1}}/>
                            <span className={"fieldvalue"} title={value}>{value}</span>
                        </div>
                    )
                }
            }
        }

        let colorFieldValue = dataRow.getValue(colorFieldId) ?? $t("na");
        return (
            <div>
                <div className={"singlemainfield"}>
                    <div className={isValidNumber(colorFieldValue) || String(colorFieldValue).length < 8 ? "numvalue": "textvalue"}>
                        {colorFieldValue}
                    </div>
                    <div className={"name"}>
                        {dataset.getFieldDefById(colorFieldId)?.name}
                    </div>
                </div>
                {
                    tooltipRows?.length > 0 &&
                    <div className={"tooltipfields"}>
                        {tooltipRows}
                    </div>
                }
            </div>
        )
    }

    private renderSinglePoint = () => {
        let layerDef = this.props.layerDef;
        let dataset = layerDef.dataset;
        let datasetRows = this.props.data;
        let selectedRow = datasetRows[this.state.selectedIndex];

        const symbol = selectedRow.getIdent();

        return (
            <Fragment>
                <div className={"nameheader"}>
                    <div className={symbol?.length <= 18 ? "symbol" : "smaller-symbol"}>
                        {symbol}
                    </div>
                    <div className={"desc"}>
                        {selectedRow.getDesc()}
                    </div>
                </div>
                {
                    this.state.showDetails ? this.renderDetailedView(selectedRow, dataset) : this.renderSimpleView(selectedRow, dataset)
                }
            </Fragment>
        )
    }

    private renderPointNavigation = () => {
        let midEl;
        let pointsCount = this.props.data.length;
        let selectedIndex = this.state.selectedIndex;

        if (pointsCount <= 10) {
            midEl = [];
            for (let i = 0; i < pointsCount; i++) {
                midEl.push(
                    <DotIcon css={(theme: Theme) => css`
                      cursor: pointer;
                      color: ${selectedIndex === i ? theme.selectionColor : theme.textColor.default};
                    `}
                             onClick={(e) => {
                                 e.stopPropagation();
                                 this.setState({selectedIndex: i});
                             }}/>
                );
            }
        } else {
            midEl = <span><b>{selectedIndex + 1}</b> of <b>{pointsCount}</b> points</span>;
        }

        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginTop: "0.25em" }}>
                <Button
                    minimal
                    title="Previous"
                    disabled={selectedIndex <= 0}
                    onClick={(e) => {
                        e.stopPropagation();
                        this.setState({selectedIndex: selectedIndex - 1});
                    }}
                >
                    <CaretLeftIcon />
                </Button>
                {midEl}
                <Button
                    minimal
                    title="Previous"
                    disabled={selectedIndex >= pointsCount - 1}
                    onClick={(e) => {
                        e.stopPropagation();
                        this.setState({selectedIndex: selectedIndex + 1});;
                    }}
                >
                    <CaretRightIcon />
                </Button>
            </div>
        )
    }

    private renderClusterListOfPoints = () => {
        let layerDef = this.props.layerDef;
        let dataset = layerDef.dataset;
        let colorFieldId = layerDef.config.colorFieldId;
        let datasetRows = this.props.data;
        const buckets = dataset.createBucketsByField(colorFieldId, layerDef.config.colorPalette.length);

        let pointValueArr = [];
        let pinned = this.props.pinned;
        for (let i = 0 ; i < datasetRows.length; i++) {
            const row = datasetRows[i];
            const color = LayerUtil.calcFillColor(layerDef, row, buckets);
            pointValueArr.push(
                <div
                    css={(theme: Theme)=>css`
                      display: flex;
                      flex-direction: row;
                      align-items: center;
                      padding: 0.2em 0;
                      &:hover {
                        cursor: pointer;
                        background-color: ${theme.listItem.selected.backgroundColor};
                      }
                    `}
                    onClick={() => {
                        if (this.props.pinned) {
                            this.setState({selectedIndex: i});
                        }
                    }}
                >
                    <XIcon icon={IconInfo.CIRCLE} iconSize={"0.9em"} fill={color.toHex()} style={{paddingRight: "0.25em"}} />
                    <span className={"fieldname"}>
                        {row.getIdent()}
                    </span>
                    <div style={{flexGrow: 1}} />
                    <span className={"fieldvalue"}>
                        {row.getValue(colorFieldId)}
                    </span>
                </div>
            );
            if (!pinned && pointValueArr.length >= 10) {
                break;
            }
        }

        const name = dataset.getFieldDefById(colorFieldId).name;

        return (
            <div>
                <div className={"ellipsis"} title={name} style={{fontSize: "1.2em", fontWeight: "bold", marginBottom: "0.5em"}}>
                    {name}
                </div>
                <div style={{overflowY: "auto", maxHeight: this.props.pinned ? "20em" : "fit-content"}}>
                    {pointValueArr}
                    {
                        pointValueArr.length < datasetRows.length &&
                        <div style={{marginTop: "0.5em", fontWeight: "bold", fontSize: "0.9em"}}>
                            And {datasetRows.length - pointValueArr.length} more...
                        </div>
                    }
                </div>
            </div>
        );
    }

    private renderCluster = () => {
        let datasetRows = this.props.data;
        return (
            <Fragment>
                {
                    this.props.pinned && this.state.selectedIndex >= 0 &&
                    <div className={"showlist"}
                         onClick={() => this.setState({selectedIndex: undefined})}
                         css={(theme: Theme)=>css`
                           cursor: pointer;
                           color: ${theme.selectionColor}
                         `}>
                        <CaretLeftIcon />
                        List of the points
                    </div>
                }
                {
                    !this.props.pinned &&
                    <div className={"pointcountheader"}>
                        {datasetRows.length} points
                    </div>
                }
                {
                    this.state.selectedIndex >= 0 ? this.renderSinglePoint() : this.renderClusterListOfPoints()
                }
            </Fragment>
        )
    }

    render = () => {
        let datasetRows = this.props.data;
        let layerDef = this.props.layerDef;
        let dataset = layerDef.dataset;

        return (
            <div className={"tooltip"}
                 tabIndex={0}
                 style={{ left: this.props.x, top: this.props.y }}
                 css={(theme) => css`
                      background-color: ${theme.backgroundColor.default};
                      border: 1px solid ${theme.borderColor.normal};
                      margin-right: 0.4em;
                `}
                 role={"alert"}
                 onClick={ev => {
                     if (this.props.pinned) {
                         ev.stopPropagation();
                     }
                 }}
                 onWheel={(event) => {
                     if (this.props.pinned) {
                         if (event.deltaY < 0) {
                             if (this.state.selectedIndex > 0) {
                                 this.setState({selectedIndex: this.state.selectedIndex - 1});
                             }
                         } else {
                             if (this.state.selectedIndex < this.props.data.length - 1) {
                                 this.setState({selectedIndex: this.state.selectedIndex + 1});
                             }
                         }
                     }
                 }}
                 onKeyDown={(event) => {
                     if (event.key === "Escape" && this.props.onEscape) {
                         this.props.onEscape();
                         event.stopPropagation();
                     }
                 }}
            >
                {datasetRows.length === 1 ? this.renderSinglePoint(): this.renderCluster()}
                <div className={"datasetinfo"}>
                    <span>{$t("dataset")}</span>
                    <div style={{flexGrow: 1}} />
                    <span>{dataset.getName()}</span>
                </div>
                {
                    !this.props.pinned &&
                    <div className={"clickinfo"}>{$t("clicktopintt")}</div>
                }
                {
                    this.props.pinned &&
                    this.state.selectedIndex >= 0 &&
                    this.props.data.length > 1 &&
                    this.renderPointNavigation()
                }
            </div>
        )
    }

}